import { AdminTemplatesControllerListOrigin } from 'src/apis/backend/generated/models/adminTemplatesControllerListOrigin';

// ----------------------------------------------------------------------

export type TemplatesTab = 'templates' | 'library' | 'sections';

export type TemplatesOrigin = AdminTemplatesControllerListOrigin;

/**
 * The page's search params as they sit in the URL. A key equal to its default
 * is left out, so the common view (the White Glove feed, first page) keeps a
 * bare URL and a shared link only names what the sender actually changed.
 */
export type TemplatesSearch = {
  tab?: TemplatesTab;
  origin?: TemplatesOrigin;
  page?: number;
  orgId?: string;
  brandId?: string;
  q?: string;
};

/** The same params with every default filled in. This is what the page reads. */
export type TemplatesSearchState = {
  tab: TemplatesTab;
  origin: TemplatesOrigin;
  page: number;
  orgId?: string;
  brandId?: string;
  q?: string;
};

export const TEMPLATES_SEARCH_DEFAULTS = {
  tab: 'templates',
  origin: 'white_glove',
  page: 1,
} as const satisfies Pick<TemplatesSearchState, 'tab' | 'origin' | 'page'>;

const TABS: readonly TemplatesTab[] = ['templates', 'library', 'sections'];

const ORIGINS: readonly TemplatesOrigin[] = Object.values(AdminTemplatesControllerListOrigin);

// ----------------------------------------------------------------------

// The router JSON-parses search values, so `?q=123` arrives as a number.
function toText(value: unknown): string | undefined {
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function toPage(value: unknown): number {
  let n = Number.NaN;
  if (typeof value === 'number') n = value;
  else if (typeof value === 'string' && value.trim() !== '') n = Number(value);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : TEMPLATES_SEARCH_DEFAULTS.page;
}

/** Drops defaults and empty values so the URL only carries what differs. */
export function toTemplatesUrlSearch(state: TemplatesSearchState): TemplatesSearch {
  const out: TemplatesSearch = {};
  if (state.tab !== TEMPLATES_SEARCH_DEFAULTS.tab) out.tab = state.tab;
  if (state.origin !== TEMPLATES_SEARCH_DEFAULTS.origin) out.origin = state.origin;
  if (state.page > TEMPLATES_SEARCH_DEFAULTS.page) out.page = state.page;
  if (state.orgId) out.orgId = state.orgId;
  if (state.brandId) out.brandId = state.brandId;
  const q = state.q?.trim();
  if (q) out.q = q;
  return out;
}

/** Route `validateSearch`: anything unrecognised falls back to its default. */
export function parseTemplatesSearch(search: Record<string, unknown>): TemplatesSearch {
  return toTemplatesUrlSearch({
    tab: TABS.includes(search.tab as TemplatesTab)
      ? (search.tab as TemplatesTab)
      : TEMPLATES_SEARCH_DEFAULTS.tab,
    origin: ORIGINS.includes(search.origin as TemplatesOrigin)
      ? (search.origin as TemplatesOrigin)
      : TEMPLATES_SEARCH_DEFAULTS.origin,
    page: toPage(search.page),
    orgId: toText(search.orgId),
    brandId: toText(search.brandId),
    q: toText(search.q),
  });
}

export function resolveTemplatesSearch(search: TemplatesSearch): TemplatesSearchState {
  return {
    tab: search.tab ?? TEMPLATES_SEARCH_DEFAULTS.tab,
    origin: search.origin ?? TEMPLATES_SEARCH_DEFAULTS.origin,
    page: search.page ?? TEMPLATES_SEARCH_DEFAULTS.page,
    orgId: search.orgId,
    brandId: search.brandId,
    q: search.q,
  };
}

/** Whether any feed filter differs from the default White Glove view. */
export function hasActiveFilters(state: TemplatesSearchState): boolean {
  return (
    state.origin !== TEMPLATES_SEARCH_DEFAULTS.origin ||
    !!state.orgId ||
    !!state.brandId ||
    !!state.q?.trim()
  );
}

const ORIGIN_NOUNS: Record<TemplatesOrigin, string> = {
  white_glove: 'White Glove emails',
  self_serve: 'self-serve emails',
  all: 'emails',
};

/** Empty-state copy that names the origin, and the filters when any narrow it. */
export function emptyStateMessage(state: TemplatesSearchState): string {
  const noun = ORIGIN_NOUNS[state.origin];
  const narrowed = !!(state.orgId || state.brandId || state.q?.trim());
  return narrowed ? `No ${noun} match these filters` : `No ${noun} yet`;
}
